/**
 * Patient Data Transformers
 *
 * Converts API response shapes (ApiPatientListItem / ApiPatientDetail)
 * into the view-model `Patient` type consumed by UI components.
 *
 * This keeps the UI components untouched while allowing the store
 * to work against the real API.
 */
import type {
  Patient,
  PatientStatus,
  ApiPatientListItem,
  ApiPatientDetail,
  ApiFacility,
} from '../types';
import { formatDate } from '@tms-phoenix/ui-vue/utils/datetime.util';

/**
 * Map the API encounter acuity_level / status to the existing UI PatientStatus.
 * Falls back to 'admitted' when no encounter is present.
 */
function mapAcuityToStatus(encounter: ApiPatientListItem['encounter']): PatientStatus {
  if (!encounter) return 'STABLE';

  const acuity = (encounter.acuity_level ?? '').toUpperCase();

  if (acuity === 'LIFE_THREATENING') return 'LIFE_THREATENING';
  if (acuity === 'CRITICAL') return 'CRITICAL';
  if (acuity === 'SERIOUS' || acuity === 'IN-PROGRESS' || acuity === 'IN_PROGRESS' || acuity === 'MODERATE') return 'SERIOUS';
  if (acuity === 'STABLE' || acuity === 'ADMITTED') return 'STABLE';

  // Fall back to encounter status
  switch (encounter.status) {
    case 'ACTIVE':
      return 'STABLE';
    case 'DISCHARGED':
    case 'TRANSFERRED':
    case 'CLOSED':
      return 'SERIOUS';
    default:
      return 'STABLE';
  }
}

/**
 * Format a date-of-birth ISO string in the user's configured timezone.
 *
 * Always renders as a bare calendar date (dd/MM/yyyy) via `formatDate` —
 * no time component is included, regardless of whether the input is a
 * bare date or a full UTC ISO datetime.
 */
function formatBirthDateTime(isoDate: string): string {
  if (!isoDate) return '';
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatDate(isoDate, tz);
  } catch {
    return isoDate;
  }
}

/**
 * Build a human-readable location string from encounter location fields.
 *
 * Returns empty string intentionally -- care_unit_id and bed_id are UUIDs and
 * must not be shown raw in the UI. The store's _enrichPatients() overwrites
 * patient.location with resolved names once reference data is loaded.
 */
function buildLocationString(_item: ApiPatientListItem): string {
  return '';
}

/**
 * Map an API list item to the view-model Patient.
 */
export function apiListItemToPatient(item: ApiPatientListItem): Patient {
  const nameParts = [item.first_name, item.middle_name, item.last_name].filter(Boolean);
  return {
    id: item.patient_id,
    name: nameParts.join(' ').trim(),
    birthDateTime: formatBirthDateTime(item.date_of_birth),
    dateOfBirthIso: item.date_of_birth || undefined,
    gender: normalizeGender(item.gender),
    medicalRecordNumber: item.mrn,
    location: buildLocationString(item),
    status: mapAcuityToStatus(item.encounter),
    consent: item.donor_consent ? 'yes' : 'no',
    createdAt: '', // Not returned on list items
    updatedAt: '',
    isAssignedToMe: false, // enriched by the store after fetch
    isActive: item.is_active,
    encounterId: item.encounter?.encounter_id ?? '',
    hospitalId: item.hospital_id,
    careUnitId: item.encounter_location?.care_unit_id ?? '',
    bedId: item.encounter_location?.bed_id ?? '',
    facilityId: item.encounter_location?.facility_id || undefined,
    facilityCode: item.encounter_location?.facility_code || undefined,
    unitCode: item.encounter_location?.unit_code || undefined,
    unitName: item.encounter_location?.unit_name || undefined,
    bedNumber: item.encounter_location?.bed_number || undefined,
    firstName: item.first_name ?? '',
    lastName: item.last_name ?? '',
    middleName: item.middle_name ?? '',
    gestationalAgeWeeks: item.gestational_age_weeks ?? null,
    birthWeightGrams: item.birth_weight_grams ?? null,
  };
}

/**
 * Normalise encounter-location fields from an API detail response.
 *
 * The create/update API responses can return care_unit_id, bed_id, and
 * facility_id as flat top-level fields instead of — or in addition to — the
 * nested encounter_location object.  This helper resolves both shapes so
 * callers never need to repeat the compensation logic.
 */
function extractEncounterLocation(detail: ApiPatientDetail): {
  careUnitId: string;
  bedId: string;
  facilityId: string | undefined;
  facilityCode: string | undefined;
  unitCode: string | undefined;
  unitName: string | undefined;
  bedNumber: string | undefined;
} {
  const loc = detail.encounter_location;
  // Cast to access possible flat top-level fields not declared in the TypeScript
  // type — these are present only on create/update responses, not on list/detail.
  const flat = detail as unknown as {
    care_unit_id?: string;
    bed_id?: string;
    facility_id?: string;
  };
  return {
    careUnitId:   loc?.care_unit_id  || flat.care_unit_id  || '',
    bedId:        loc?.bed_id        || flat.bed_id        || '',
    facilityId:   loc?.facility_id   || flat.facility_id   || undefined,
    facilityCode: loc?.facility_code || undefined,
    unitCode:     loc?.unit_code     || undefined,
    unitName:     loc?.unit_name     || undefined,
    bedNumber:    loc?.bed_number    || undefined,
  };
}

/**
 * Map a full API patient detail to the view-model Patient.
 *
 * @param detail     - Raw API response (create, update, or GET /:id).
 * @param facilities - Optional pre-loaded facility list used to derive
 *                     facilityCode when it is absent from the response (e.g.
 *                     create/update responses omit facility_code).  When
 *                     omitted, facilityCode will be whatever the API returned.
 */
export function apiDetailToPatient(detail: ApiPatientDetail, facilities?: ApiFacility[]): Patient {
  const nameParts = [detail.first_name, detail.middle_name, detail.last_name].filter(Boolean);
  const loc = extractEncounterLocation(detail);

  // Derive facilityCode from the caller-supplied facility list when the API
  // response omits it (create/update responses do not include facility_code).
  const facilityCode =
    loc.facilityCode ||
    (loc.facilityId && facilities?.length
      ? facilities.find((f) => f.facility_id === loc.facilityId)?.facility_code
      : undefined);

  return {
    id: detail.patient_id,
    name: nameParts.join(' ').trim(),
    birthDateTime: formatBirthDateTime(detail.date_of_birth),
    dateOfBirthIso: detail.date_of_birth || undefined,
    gender: normalizeGender(detail.gender),
    medicalRecordNumber: detail.mrn,
    location: buildLocationString(detail),
    status: mapAcuityToStatus(detail.encounter),
    consent: detail.donor_consent ? 'yes' : 'no',
    allergies: (detail.allergies ?? []).map((a) => ({
      allergy: a.allergen_id,
      severity: a.severity_id,
      ...(a.status ? { status: a.status } : {}),
    })),
    createdAt: detail.created_at ?? '',
    updatedAt: detail.updated_at ?? '',
    isAssignedToMe: false,
    isActive: detail.is_active,
    // The GET /:id response nests encounter_id under `encounter.encounter_id`.
    // The create/update (POST/PATCH) response returns it as a flat top-level
    // field `encounter_id` — the same compensation pattern used above for
    // encounter_location fields.  Always prefer the nested form first.
    encounterId: detail.encounter?.encounter_id
      ?? (detail as ApiPatientDetail & { encounter_id?: string }).encounter_id
      ?? '',
    hospitalId: detail.hospital_id,
    careUnitId:   loc.careUnitId,
    bedId:        loc.bedId,
    facilityId:   loc.facilityId,
    facilityCode,
    unitCode:     loc.unitCode,
    unitName:     loc.unitName,
    bedNumber:    loc.bedNumber,
    motherLinkId: detail.mother_infant_link?.link_id ?? undefined,
    motherPersonId: detail.mother_infant_link?.person_id ?? undefined,
    motherName: detail.mother_infant_link
      ? [detail.mother_infant_link.mother_first_name, detail.mother_infant_link.mother_last_name].filter(Boolean).join(' ') || undefined
      : undefined,
    birthWeightGrams: detail.birth_weight_grams ?? null,
    multipleBirthInd: detail.multiple_birth_ind ?? undefined,
    multipleBirthOrd: detail.multiple_birth_ord ?? null,
    middleName: detail.middle_name ?? '',
    firstName: detail.first_name ?? '',
    lastName: detail.last_name ?? '',
    gestationalAgeWeeks: detail.gestational_age_weeks ?? null,
    patientAssignments: (detail.patient_assignments ?? []).map((a) => ({
      name: `${a.staff_first_name} ${a.staff_last_name}`.trim(),
      role: a.assignment_role,
      phone: a.staff_phone_number ?? null,
    })),
  };
}

function normalizeGender(raw: string): 'male' | 'female' | 'undetermined' {
  const g = raw.toUpperCase();
  if (g === 'M' || g === 'MALE') return 'male';
  if (g === 'F' || g === 'FEMALE') return 'female';
  return 'undetermined';
}
